import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Form,
  ModalFooter,
  SelectSearch,
} from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import { useAdminUsersQuery } from "../../../users/api/admin-user";
import { ComplexCombobox } from "../../../../shared";
import {
  assignComplexAdminSchema,
  type AssignComplexAdminFormData,
} from "../../schemas";

interface AssignComplexAdminFormProps {
  onSubmit: (data: AssignComplexAdminFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const AssignComplexAdminForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
}: AssignComplexAdminFormProps) => {
  const { translate } = useLocalization();

  // Backend exige que o alvo seja tenant role 'user'. A listagem do Admin já
  // devolve apenas usuários operacionais (role=user) — owner/ownerViewer não
  // aparecem —, então não há o que filtrar aqui.
  const { data: usersPage } = useAdminUsersQuery({ page: 1, limit: 100 });

  const userOptions = useMemo(
    () =>
      (usersPage?.data ?? []).map((u) => ({
        label: `${u.name} — ${u.email}`,
        value: u.id,
      })),
    [usersPage],
  );

  const form = useForm<AssignComplexAdminFormData>({
    resolver: zodResolver(assignComplexAdminSchema(translate)),
    defaultValues: { userId: "", complexId: "" },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="px-[32px] py-[16px] flex flex-col gap-4 min-w-[420px]">
          <SelectSearch
            control={form.control}
            name="userId"
            label={translate("admin.access.form.label.user")}
            placeholder={translate(
              "admin.access.form.placeholder.selectUser",
            )}
            options={userOptions}
            clearable
          />
          <ComplexCombobox
            control={form.control}
            name="complexId"
            label={translate("admin.access.form.label.complex")}
            placeholder={translate(
              "admin.access.form.placeholder.selectComplex",
            )}
            clearable
          />
        </div>
        <ModalFooter
          footer={
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {translate("admin.access.complexAdmins.button.assign")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("admin.users.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
